import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { PaginationComponent } from '@shared/components/common/pagination/pagination.component'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { ColumnsFilledOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/stocks/columns-filled-orders.enum'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { DatePipe, NgTemplateOutlet } from '@angular/common'
import { PricePipe } from '@shared/pipes/price.pipe'
import { TranslatePipe } from '@ngx-translate/core'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { FilledOrdersActionsComponent } from './common/filled-orders-actions/filled-orders-actions.component'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { TuiSkeleton } from '@taiga-ui/kit'
import { TableComponent } from '@shared/components/common/table/table.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-filled-orders',
	templateUrl: './filled-orders.component.html',
	styleUrl: './filled-orders.component.scss',
	imports: [
		PaginationComponent,
		HighlightDirective,
		NgTemplateOutlet,
		PricePipe,
		TranslatePipe,
		ProfitPipe,
		DatePipe,
		TableColumnComponent,
		TableActionsComponent,
		TableFooterComponent,
		FilledOrdersActionsComponent,
		SecretDirective,
		QuantityPipe,
		TuiSkeleton,
		TableComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilledOrdersComponent implements OnInit, OnDestroy, OnChanges {
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly columnsFilledOrdersEnum: typeof ColumnsFilledOrdersEnum = ColumnsFilledOrdersEnum

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject()

	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	protected isLoadingOrders: WritableSignal<boolean> = signal(false)
	protected ordersResponse: WritableSignal<OrdersResponseInterface | null | undefined> = signal(null)
	protected ordersParams: WritableSignal<OrderParamsInterface | undefined> = signal(undefined)

	protected statesColumns: WritableSignal<ColumnInterface<ColumnsFilledOrdersEnum>[]> = signal([])
	protected needScrollToTop: WritableSignal<boolean> = signal(false)
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _launchFilledOrders(): void {
		this._tradingLauncherService
			.launchOrders$(this.orderStateEnum.FILLED)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				([ordersResponse, isLoading, orderParams, isNeedUpdate]: [
					OrdersResponseInterface | null | undefined,
					boolean,
					OrderParamsInterface | undefined,
					boolean,
				]): void => {
					if (this.isLoadingOrders() !== isLoading) this.needScrollToTop.set(true)

					this.ordersResponse.set(ordersResponse)
					this.isLoadingOrders.set(isLoading)
					this.ordersParams.set(orderParams)

					setTimeout((): void => this.needScrollToTop.set(false))
					if (isNeedUpdate) {
						this._ordersStocksHelperService.isNeedUpdateOrderResponseMap
							.get(this.orderStateEnum.FILLED)
							?.next(false)
						this._getFilledOrders()
					}
				},
			)
	}

	private _getFilledOrders(): void {
		this._ordersStocksService.getAllOrders(this.orderStateEnum.FILLED).pipe(takeUntil(this._destroy$)).subscribe()
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.stocks.filled.columns])
	}

	protected onPageChange(newPage: number): void {
		const ordersParams$: BehaviorSubject<OrderParamsInterface> | undefined =
			this._ordersStocksHelperService.ordersParamsMap.get(this.orderStateEnum.FILLED)
		if (!ordersParams$) return

		ordersParams$.next({ ...ordersParams$.getValue(), page: newPage })

		this._getFilledOrders()
		this.isLoadingOrders.set(true)
	}

	protected setLiveTicker(symbol: string): void {
		this._widgetsService.setLiveTickerToDashboard(symbol)
		this._liveTickerService.launchLiveTicker(symbol)
	}

	protected calcQtyAvailable(order: OrderInterface): number {
		return (order.qty ?? 0) * (order.filled_avg_price ?? 0)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) this._updateStatesColumns()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._launchFilledOrders()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
